<template>
    <AppLayout>
        <div class="max-w-7xl mx-auto px-4 py-6">
            <!-- Page Title -->
            <div class="mb-6">
                <h1 class="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                    {{ isBounce ? $tc('LBL_PAYMENT_CHEQUE_BOUNCE', 1) : $tc('LBL_CHEQUE', 2) }}
                </h1>
                <!-- Breadcrumb -->
                <nav
                    class="text-sm text-gray-500 mb-6"
                    aria-label="Breadcrumb"
                >
                    <ol class="flex items-center space-x-2">
                        <li class="flex items-center">
                            <inertia-link
                                :href="route('landing')"
                                class="text-green-600 hover:underline"
                            >
                                {{ $t('LBL_HOME') }}
                            </inertia-link>
                            <svg
                                class="w-3 h-3 mx-2 text-green-400"
                                fill="currentColor"
                                viewBox="0 0 320 512"
                            >
                                <path
                                    d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"
                                />
                            </svg>
                        </li>
                        <li class="text-gray-700">
                            {{ $tc('LBL_PAYMENT_CHEQUE', 1) }}
                        </li>
                    </ol>
                </nav>
            </div>
            <!-- breadcrumb end -->

            <div
                class="w-full bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-overflow"
            >
                <!-- Search & Action Bar -->
                <div class="flex flex-wrap items-center justify-between gap-4 p-4 sm:flex-nowrap">
                    <!-- Action Buttons -->
                    <t-rich-select
                        v-model="selectedBank"
                        class="w-60"
                        :close-on-select="false"
                        :options="banks"
                        :clearable="true"
                        value-attribute="id"
                        text-attribute="name"
                        variant="success"
                        placeholder="Filter by bank"
                    />
                    <t-input
                        id="cheque-number-search-box"
                        v-model="chequeNumber"
                        variant="success"
                        placeholder="Search by cheque number"
                    />
                    <t-input
                        id="customer-name-search-box"
                        v-model="customerName"
                        variant="success"
                        placeholder="Search by customer name"
                    />
                    <t-button
                        class="inline-flex items-center rounded-md"
                        variant="success"
                        @click="fetchPageData()"
                    >
                        Search
                    </t-button>
                </div>

                <div
                    class="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-none shadow-md"
                >
                    <div class="overflow-x-auto">
                        <!-- Table content to list the Products -->
                        <table
                            class="min-w-[700px] w-full table-auto text-sm sm:text-base text-left text-gray-700 dark:text-gray-300"
                        >
                            <thead class="bg-gray-100 dark:bg-gray-700 text-sm font-semibold">
                                <tr>
                                    <th class="px-4 py-3">#</th>
                                    <th class="px-4 py-3">ORDER NUMBER</th>
                                    <th class="px-4 py-3">BANK</th>
                                    <th class="px-4 py-3">CHEQUE NUMBER</th>
                                    <th class="px-4 py-3">CHEQUE AMOUNT</th>
                                    <th class="px-4 py-3">DATE</th>
                                    <th class="px-4 py-3">ACTIONS</th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-for="(row, rowIndex) in checkPayments.data">
                                    <tr
                                        class="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition duration-200"
                                    >
                                        <td class="px-4 py-3">{{ rowIndex + 1 + (currentPage - 1) * perPage }}</td>
                                        <td class="px-4 py-3">{{ row.bill.bill_number }}</td>
                                        <td class="px-4 py-3">{{ row.bank.name }}</td>
                                        <td class="px-4 py-3">{{ row.check_number }}</td>
                                        <td class="px-4 py-3">{{ config(common.currency) }} {{ row.check_amount }}</td>
                                        <td class="px-4 py-3">{{ row.created_at | dateLabel }}</td>
                                        <td class="px-4 py-3">
                                            <div v-if="!isBounce">
                                                <div
                                                    v-if="row.is_bounced"
                                                    class="inline-flex px-3 py-1 text-red-900 bg-red-200 border rounded cursor-not-allowed"
                                                >
                                                    {{ $tc('BTN_BOUNCE', 2) }}
                                                </div>
                                                <div
                                                    v-else
                                                    class="inline-flex px-3 py-1 text-white bg-red-500 border rounded cursor-pointer"
                                                    @click="showBounceModal(row)"
                                                >
                                                    {{ $tc('BTN_BOUNCE', 1) }}
                                                </div>
                                            </div>
                                            <div
                                                v-else-if="isBounce == true"
                                                class="inline-flex px-3 py-1 text-red-900 bg-red-200 border rounded cursor-not-allowed"
                                            >
                                                {{ $tc('BTN_BOUNCE', 2) }}
                                            </div>
                                        </td>
                                    </tr>
                                </template>
                                <tr
                                    v-if="!checkPayments.data.length"
                                    class="text-center"
                                >
                                    <td
                                        colspan="7"
                                        class="px-4 py-4 text-gray-500 dark:text-gray-400"
                                    >
                                        {{ $t('LBL_DATA_FOUND') }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div
                        v-if="checkPayments.data.length"
                        class="flex justify-center items-center bg-gray-100 dark:bg-gray-800 px-4 py-4 border-t dark:border-gray-700"
                    >
                        <t-pagination
                            v-model="currentPage"
                            variant="rounded"
                            :total-items="totalRows"
                            :per-page="perPage"
                            :limit="limit"
                            :disabled="!checkPayments.data.length"
                            @change="fetchPageData"
                        />
                    </div>
                    <p class="sm:hidden text-xs text-center text-gray-500 py-2">
                        ↔ Swipe left/right to scroll the table
                    </p>
                </div>
            </div>

            <t-modal
                ref="chequeModal"
                v-model="bounceModal"
                header="Cheque Bounce"
            >
                <div
                    v-if="currentCheque"
                    class="space-y-5 p-2 text-gray-800"
                >
                    <div class="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <div class="font-medium text-gray-600">Bill Number</div>
                            <div class="text-base text-gray-900">{{ currentCheque.bill.bill_number }}</div>
                        </div>

                        <div>
                            <div class="font-medium text-gray-600">Cheque Number</div>
                            <div class="text-base text-gray-900">{{ currentCheque.check_number }}</div>
                        </div>

                        <div>
                            <div class="font-medium text-gray-600">Cheque Amount</div>
                            <div class="text-base text-gray-900">Rs. {{ currentCheque.check_amount }}</div>
                        </div>
                    </div>

                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1"
                            >Remarks <span class="text-red-500">*</span></label
                        >
                        <t-textarea
                            v-model="remarks"
                            placeholder="Write reason for cheque bounce..."
                            rows="3"
                            class="w-full"
                        />
                    </div>
                </div>

                <template #footer>
                    <div class="flex justify-end gap-3 px-2 py-2 border-t">
                        <t-button
                            type="button"
                            variant="success"
                            :disabled="!remarks"
                            @click="bounceCheque"
                        >
                            Confirm
                        </t-button>
                        <t-button
                            type="button"
                            variant="secondary"
                            @click="$refs.chequeModal.hide()"
                        >
                            Cancel
                        </t-button>
                    </div>
                </template>
            </t-modal>
        </div>
    </AppLayout>
</template>

<script>
    import axios from 'axios';
    import AppLayout from '@/Layouts/AppLayout.vue';
    import common from '@/common';

    export default {
        name: 'Cheques',

        components: { AppLayout },

        props: {
            checkPayments: {
                type: Object,
                required: true,
            },
            banks: {
                type: Array,
                required: true,
            },
            isBounce: {
                type: Boolean,
                required: false,
            },
            appliedFilters: {
                type: Object,
                required: true,
            },
        },

        data() {
            return {
                currentCheque: null,
                common,
                currentPage: this.checkPayments.current_page,
                limit: 10,
                perPage: this.checkPayments.per_page,
                totalRows: this.checkPayments.total,
                chequeNumber: null,
                customerName: null,
                bankId: null,
                isBounced: null,
                selectedBank: null,
                bounceModal: false,
                remarks: null,
                disabled: false,
            };
        },

        mounted() {
            if (this.appliedFilters.chequeNumber) {
                this.chequeNumber = this.appliedFilters.chequeNumber;
            }

            if (this.appliedFilters.customerName) {
                this.customerName = this.appliedFilters.customerName;
            }

            if (this.appliedFilters.bankId) {
                this.selectedBank = Number(this.appliedFilters.bankId);
            }
            this.disabled = this.checkPayments.data.length === 0;
        },

        methods: {
            showBounceModal(cheque) {
                if (cheque.is_bounced) {
                    this.$dialog.confirm({
                        title: 'Warning',
                        text: `This cheque is already flagged as bounch : ${cheque.check_number}?`,
                    });
                } else {
                    this.bounceModal = true;
                    this.currentCheque = cheque;
                }
            },

            hideBounceModal() {
                this.bounceModal = false;
                this.currentCheque = null;
                this.remarks = null;
            },

            bounceCheque() {
                this.$dialog
                    .confirm({
                        title: 'Confirm',
                        text: `Are you sure you want to bounce the cheque with number : ${this.currentCheque.check_number}?`,
                    })
                    .then((result) => {
                        if (result.isOk) {
                            axios
                                .post(route('checkPayment.cancelPayment', this.currentCheque), {
                                    remarks: this.remarks,
                                })
                                .then(() => {
                                    this.hideBounceModal();
                                });
                        }
                    });
            },

            fetchPageData(newPage = 1) {
                this.$inertia.get(
                    route('payment.cheque', {
                        page: newPage,
                        chequeNumber: this.chequeNumber,
                        customerName: this.customerName,
                        bankId: this.selectedBank,
                    }),
                );
            },
        },
    };
</script>
